import type { CMSStory, CmsStoryContent, CmsCopy } from "../adapters";
import { type ApiConfiguration } from "./shared";

const getTranslatedSlug = (slug: string, config: ApiConfiguration) => {
  if (config.locale === "en-CH") return slug;

  const arr = slug.split("/");
  arr.shift();
  return arr.join("/");
};

export const localeCopyApi = (config: ApiConfiguration) => ({
  async getCmsCopy(storiesPrefixes: string[], signal: AbortSignal): Promise<CmsCopy> {
    const response = await config.cmsService.getAll<CmsStoryContent>(storiesPrefixes, signal);

    const processLocaleCopy = (response: CMSStory<CmsStoryContent>[]): Record<string, string> =>
      response.reduce((result: Record<string, string>, obj) => {
        const slug = getTranslatedSlug(obj.full_slug, config);
        const textProperties: { [key: string]: string | undefined } = {
          [slug]: obj.content.Text ?? obj.content.DisplayedName,
          [`${slug}/title`]: obj.content.Title,
          [`${slug}/sub-text`]:
            typeof obj.content.SubText === "string"
              ? obj.content.SubText
              : obj.content.SubText?.content?.[0]?.content?.[0]?.text,
          [`${slug}/box-title`]: obj.content.BoxTitle,
          [`${slug}/content-example`]: obj.content.ContentExample,
          [`${slug}/main-text`]: obj.content.MainText,
          [`${slug}/tooltip-text`]: obj.content.TooltipText,
          [`${slug}/maintenances`]: JSON.stringify(obj.content.Maintenances?.at(-1)),
        };

        Object.entries(textProperties).forEach(([key, value]) => {
          if (value) result[key] = value;
        });

        return result;
      }, {});

    const processOtherStories = (response: CMSStory<CmsStoryContent>[]): Record<string, CmsStoryContent> => {
      return response.reduce((result: Record<string, CmsStoryContent>, obj) => {
        const slug = getTranslatedSlug(obj.full_slug, config);
        const identifier = obj.full_slug.split("/").pop() || "";
        result[slug] = { ...obj.content, slug: identifier };
        return result;
      }, {});
    };

    const cmsCopy = {
      localeCopy: processLocaleCopy(response),
      otherStories: processOtherStories(response),
    };

    return cmsCopy;
  },
});
